// The documents page's URL-owned state, in one place: which folder and doc
// are open (`?folder=` / `?doc=`), which drawer dialog is up (`?compare=`),
// `?compare=`), and the search term (`?q=`, debounced). Everything here is
// shareable and reload-safe by construction; components never touch
// searchParams directly.

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { idAfterFolderRename } from '../utils/folderPath';

/** A positive-integer URL param, or null for anything else. */
function numberParam(raw: string | null): number | null {
  const n = Number(raw);
  return raw !== null && Number.isInteger(n) && n > 0 ? n : null;
}

export function useDocumentsUrl() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedFolder = searchParams.get('folder') ?? 'all';
  const selectedFileId = searchParams.get('doc'); // open drawer lives in the URL so it's deep-linkable

  const [searchInput, setSearchInput] = useState(() => searchParams.get('q') ?? '');
  const searchQuery = useDebouncedValue(searchInput, 250);

  // The drawer's compare dialog lives in the URL too:
  // ?compare=<version> (compare pins the latest on the left; the param names
  // the right side).
  const compareVersion = numberParam(searchParams.get('compare'));

  // A folder change is a history step, so Back walks up the path you came.
  // `replace` for leaving a folder that is gone: Back must not lead into it.
  function setSelectedFolder(id: string, { replace = false } = {}) {
    setSearchParams((p) => {
      p.set('folder', id);
      p.delete('doc');
      p.delete('compare');
      return p;
    }, { replace });
  }

  function setSelectedFileId(id: string | null) {
    setSearchParams((p) => {
      if (id) p.set('doc', id);
      else p.delete('doc');
      // Modal state belongs to the doc it was opened on — never carries over.
      p.delete('compare');
      return p;
    }, { replace: true });
  }

  // A renamed or moved folder changes the ids of everything under it: the open
  // folder and doc follow to their new ids. The write settles seconds after it
  // started, and a setSearchParams callback is handed the URL of the render
  // that made it — so this goes through the latest render's, against the URL as
  // it is now. Once the page is gone there is none, and nothing is written.
  const liveSetSearchParams = useRef<typeof setSearchParams | null>(null);
  useEffect(() => {
    liveSetSearchParams.current = setSearchParams;
    return () => { liveSetSearchParams.current = null; };
  }, [setSearchParams]);
  function followFolderRename(oldId: string, newId: string) {
    liveSetSearchParams.current?.((p) => {
      for (const key of ['folder', 'doc']) {
        const moved = idAfterFolderRename(p.get(key) ?? '', oldId, newId);
        if (moved) p.set(key, moved);
      }
      return p;
    }, { replace: true });
  }

  function setVersionParam(key: 'compare', version: number | null) {
    setSearchParams((p) => {
      if (version !== null) p.set(key, String(version));
      else p.delete(key);
      return p;
    }, { replace: true });
  }


  // Mirror the settled search term into the URL (debounced via `searchQuery`).
  useEffect(() => {
    setSearchParams((p) => {
      if (searchQuery) p.set('q', searchQuery); else p.delete('q');
      return p;
    }, { replace: true });
  }, [searchQuery, setSearchParams]);

  return {
    selectedFolder, setSelectedFolder,
    selectedFileId, setSelectedFileId,
    compareVersion, setVersionParam,
    searchInput, setSearchInput, searchQuery,
    followFolderRename,
  };
}